import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { XMarkIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

const IOS_STORE_URL = process.env.REACT_APP_IOS_APP_STORE_URL || '';
const ANDROID_STORE_URL = process.env.REACT_APP_ANDROID_PLAY_STORE_URL
  || 'https://play.google.com/store/apps/details?id=com.teamcentral.app';

const DISMISS_KEY = 'mobile-app-banner-dismissed-at';
const DISMISS_DAYS = 30;

const getMobilePlatform = () => {
  const ua = navigator.userAgent || '';
  // iPadOS reports itself as a Mac; touch support gives it away
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return null;
};

const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
    return dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// Mobile-browser-only banner pointing to the native app's store listing.
// Desktop browsers and the native app itself never see it.
const MobileAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const platform = getMobilePlatform();
  const storeUrl = platform === 'ios' ? IOS_STORE_URL : platform === 'android' ? ANDROID_STORE_URL : '';

  useEffect(() => {
    // Suppress the browser's own "Install app" (PWA) prompt so it doesn't compete with the store apps
    const suppressInstallPrompt = (e) => e.preventDefault();
    window.addEventListener('beforeinstallprompt', suppressInstallPrompt);

    if (!Capacitor.isNativePlatform() && storeUrl && !wasRecentlyDismissed()) {
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', suppressInstallPrompt);
  }, [storeUrl]);

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode) — banner simply returns next visit
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 flex items-center gap-3">
        <DevicePhoneMobileIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">FieldHouse is better in the app</p>
          <p className="text-xs text-gray-600">
            Get it on the {platform === 'ios' ? 'App Store' : 'Google Play'}
          </p>
        </div>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDismiss}
          className="bg-primary-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-primary-700 transition-colors"
        >
          Get App
        </a>
        <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default MobileAppBanner;
